/**
 * Bill of Lading Parties Fields Component
 *
 * Renders the parties section of the B/L form. Mirrors OBPartiesFields exactly
 * but with the full B/L party type set (adds ENDORSEE, ALSO_NOTIFY_PARTY,
 * FREIGHT_PAYER relative to OB) and adds editable identifyingCodes nested array.
 *
 * Party types:
 *   SHIPPER, CONSIGNEE, CARRIER, NOTIFY_PARTY, BOOKING_AGENT,
 *   ENDORSEE, ALSO_NOTIFY_PARTY, FREIGHT_PAYER, BENEFICIAL_CARGO_OWNER
 *
 * Uses react-hook-form's useFieldArray for dynamic array management.
 */

import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { COUNTRY_OPTIONS } from '../../constants/countries';
import { getFieldIssues } from '../../utils/confidenceUtils';

const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * B/L party type options — superset of OB party types
 */
const PARTY_TYPES = [
  { value: 'SHIPPER', label: 'Shipper' },
  { value: 'CONSIGNEE', label: 'Consignee' },
  { value: 'CARRIER', label: 'Carrier' },
  { value: 'NOTIFY_PARTY', label: 'Notify Party' },
  { value: 'BOOKING_AGENT', label: 'Booking Agent' },
  { value: 'ENDORSEE', label: 'Endorsee' },
  { value: 'ALSO_NOTIFY_PARTY', label: 'Also Notify Party' },
  { value: 'FREIGHT_PAYER', label: 'Freight Payer' },
  { value: 'BENEFICIAL_CARGO_OWNER', label: 'Beneficial Cargo Owner' }
];

/**
 * Nested dynamic array for party identifying codes
 * Rendered inside each party card when the party is expanded.
 */
function IdentifyingCodesSection({ partyIndex, control, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(true);

  const { fields, append, remove } = useFieldArray({
    control,
    name: `parties.${partyIndex}.identifyingCodes`
  });

  const addCode = () => {
    append({ codeType: '', value: '' });
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Identifying Codes ({fields.length})
        </button>
        {isEditMode && isOpen && (
          <Button variant="secondary" size="sm" onClick={addCode}>
            + Add Code
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="space-y-2 ml-4">
          {fields.length === 0 && (
            <div className="text-center py-2 text-gray-400 text-sm bg-gray-50 rounded">
              No identifying codes.
            </div>
          )}
          {fields.map((code, cIdx) => (
            <div key={code.id} className="flex items-end gap-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input
                  label="Code Type"
                  {...register(`parties.${partyIndex}.identifyingCodes.${cIdx}.codeType`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `parties[${partyIndex}].identifyingCodes[${cIdx}].codeType`)}
                  placeholder="e.g., EORI, SCAC"
                />
                <Input
                  label="Value"
                  {...register(`parties.${partyIndex}.identifyingCodes.${cIdx}.value`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `parties[${partyIndex}].identifyingCodes[${cIdx}].value`)}
                  placeholder="Code value"
                />
              </div>
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => remove(cIdx)}
                  className="text-red-500 hover:text-red-700 text-sm mb-4"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * BLPartiesFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLPartiesFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parties'
  });

  const addParty = () => {
    append({
      type: 'SHIPPER',
      name: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      identifyingCodes: [],
      contactName: '',
      phone: '',
      email: ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">Parties</h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addParty}>
            + Add Party
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No parties added yet. Click "Add Party" to add shipper, consignee, or other party information.
        </div>
      )}

      {/* Party entries */}
      {fields.map((field, index) => (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Party header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Party #{index + 1}</span>
            {isEditMode && (
              <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700 text-sm">
                Remove
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Party Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Type
                  <IssueIndicator issues={getFieldIssues(issues, `parties[${index}].type`)} />
                </span>
              </label>
              <select
                {...register(`parties.${index}.type`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {PARTY_TYPES.map(pt => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div className="md:col-span-2">
              <Input
                label="Name"
                {...register(`parties.${index}.name`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].name`)}
                placeholder="Company or person name"
              />
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <Input
                label="Address Line 1"
                {...register(`parties.${index}.address.addressLine1`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine1`)}
                placeholder="Street address"
              />
            </div>
            <div className="md:col-span-2">
              <Input
                label="Address Line 2"
                {...register(`parties.${index}.address.addressLine2`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine2`)}
                placeholder="Suite, building, floor (optional)"
              />
            </div>
            <div className="md:col-span-2">
              <Input
                label="Address Line 3"
                {...register(`parties.${index}.address.addressLine3`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].address.addressLine3`)}
                placeholder="Additional address info (optional)"
              />
            </div>

            <Input
              label="City"
              {...register(`parties.${index}.address.city`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `parties[${index}].address.city`)}
            />

            <Input
              label="State/Province"
              {...register(`parties.${index}.address.state`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `parties[${index}].address.state`)}
            />

            <Input
              label="Postal Code"
              {...register(`parties.${index}.address.postalCode`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `parties[${index}].address.postalCode`)}
            />

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Country
                  <IssueIndicator issues={getFieldIssues(issues, `parties[${index}].address.country`)} />
                </span>
              </label>
              <select
                {...register(`parties.${index}.address.country`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {COUNTRY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-3 mt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Contact Name"
                {...register(`parties.${index}.contactName`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].contactName`)}
              />
              <Input
                label="Phone"
                {...register(`parties.${index}.phone`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].phone`)}
              />
              <Input
                label="Email"
                {...register(`parties.${index}.email`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `parties[${index}].email`)}
              />
            </div>
          </div>

          {/* Identifying Codes — editable nested array */}
          <IdentifyingCodesSection
            partyIndex={index}
            control={control}
            register={register}
            isEditMode={isEditMode}
            issues={issues}
          />
        </div>
      ))}
    </div>
  );
}

export default BLPartiesFields;
